import type { FC } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { finCalcDataFieldAtom } from '../../Atoms/FinCalcData.atom';
import { finCalcInteractedWithAtom } from '../../Atoms/FinCalcInteractedWith.atom';
import { isEmptyPayloadAtom } from '../../Atoms/isEmptyPayload.atom';
import type { FieldDescriptor } from '../../Entities/FinCalcFields.entity';
import { CheckboxButtonElement } from '../CheckboxButton/CheckboxButton.element';
import { DropdownElement } from '../Dropdown.element';
import { InputElement } from '../Input/Input.element';
import { RangeInputElement } from '../Range/Range.element';

import styles from './FieldGroup.module.css';

const elementMap: Record<FieldDescriptor['type'], FC<any>> = {
  input: InputElement,
  select: DropdownElement,
  checkbox: CheckboxButtonElement,
  range: RangeInputElement,
};

export const FieldGroup = ({
  fields,
  useDynamicGrid,
}: {
  fields: FieldDescriptor[];
  useDynamicGrid?: boolean;
}) => {
  const [_, setIsEmpty] = useAtom(isEmptyPayloadAtom);
  const [, setFinCalcInteractedWith] = useAtom(finCalcInteractedWithAtom);

  const handleButtonClick = () => {
    setFinCalcInteractedWith(true);
    setIsEmpty(false);
  };

  const gridTemplateColumns = useDynamicGrid ? `repeat(${fields.length}, 1fr)` : undefined;

  return (
    <div
      className={styles.fieldGroup}
      style={gridTemplateColumns ? { gridTemplateColumns } : undefined}
      onClick={handleButtonClick}
    >
      {fields?.map(fieldDescriptor => (
        <Field
          fieldDescriptor={fieldDescriptor}
          key={`${fieldDescriptor.id}-${fieldDescriptor.type}`}
        />
      ))}
    </div>
  );
};

const Field = ({ fieldDescriptor }: { fieldDescriptor: FieldDescriptor }) => {
  const fieldData = useAtomValue(finCalcDataFieldAtom(fieldDescriptor));
  const { width, id, isActive } = fieldData;

  const MappedElement = elementMap[fieldDescriptor.type];

  if (!MappedElement) return null;
  if (!isActive) return null;

  return (
    <div className={styles.field} data-width={width} data-id={id}>
      <MappedElement {...fieldData} />
    </div>
  );
};
